<script setup lang="ts">
import { AbcResponsiveTable } from "@ordbogen-npm/abc-dsk";
import { computed, ref, watch } from "vue";

import { useArticleFont } from "@/common/composables/use-article-font.composable";
import {
  buildCountTypeHeader,
  buildGenderHeader,
  casusLabels
} from "@/components/article/dictsys/inflectionSchemes/german/labels";
import InflectionSchemeCell from "@/components/article/dictsys/inflectionSchemes/InflectionSchemeCell.vue";
import InflectionSchemeTabs from "@/components/article/dictsys/inflectionSchemes/InflectionSchemeTabs.vue";
import { makeInflectionCell } from "@/helpers/dictsys-inflection-scheme/inflection-scheme.helper";
import { useSettingsStore } from "@/stores";
import type {
  DictsysGermanNounInflectionScheme,
  DictsysInflectionSchemeContent,
  DictsysInflectionSchemeGermanNounArticleType,
  DictsysInflectionSchemeGermanNounCases,
  DictsysInflectionSchemeGermanNounGenders
} from "@/types/article-formats/dictsys";
import type { InflectionScheme, InflectionTable, TabLabel } from "@/types/inflection-scheme.type";

const props = defineProps<{
  inflectionScheme: NonNullable<DictsysGermanNounInflectionScheme["noun"]>;
}>();

const emits = defineEmits<{
  showTab: [name: string];
}>();

const settingsStore = useSettingsStore();

const wordClassLevel = computed(() => settingsStore.activeWordClassLevel);

type ArticleType = "definite" | "indefinite";

const countTypeTabs = computed<TabLabel<ArticleType>[]>(() => [
  {
    key: "definite",
    labels: {
      danish: "Bestemt kendeord",
      latinAsComment: "Bestemt artikel",
      latin: "Bestemt artikel"
    }
  },
  {
    key: "indefinite",
    labels: {
      danish: "Ubestemt kendeord",
      latinAsComment: "Ubestemt artikel",
      latin: "Ubestemt artikel"
    }
  }
]);
const activeCountTypeTabKey = ref<ArticleType>("definite");

watch(
  () => activeCountTypeTabKey.value,
  (showTabKey) => {
    emits("showTab", showTabKey);
  }
);

const makeCaseRow = (
  schemes: DictsysInflectionSchemeGermanNounArticleType[],
  articleType: ArticleType,
  casus: keyof DictsysInflectionSchemeGermanNounCases
): DictsysInflectionSchemeContent[][] => {
  const row = [];

  for (let i = 0; i < schemes.length; i++) {
    const genderScheme = schemes[i];
    const isLastGender = i === schemes.length - 1;

    const singular = genderScheme[articleType][casus].singular ?? makeInflectionCell("-");
    const plural = genderScheme[articleType][casus].plural ?? makeInflectionCell("-");

    row.push(singular);
    if (isLastGender) {
      row.push(plural);
    }
  }

  return row;
};

const makeArticleTypeTable = (
  scheme: DictsysInflectionSchemeGermanNounGenders,
  articleType: ArticleType
): InflectionTable => {
  // When there are multiple genders, we want the scheme to have a header, like:
  // 'Kasus' | 'masculine' | 'feminine' | 'neutral'  | 'plural'
  // If there is only 1 gender we can simply have:
  // 'Kasus' | 'singular' | 'plural'

  const isMultiGenderScheme = Object.keys(scheme).length > 1;
  const genderSchemes = [
    scheme?.masculine ?? undefined,
    scheme?.feminine ?? undefined,
    scheme?.neutral ?? undefined,
    scheme?.void ?? undefined
  ].filter((s): s is DictsysInflectionSchemeGermanNounArticleType => Boolean(s));

  const genderHeaders = [
    scheme?.masculine ? "masculine" : undefined,
    scheme?.feminine ? "feminine" : undefined,
    scheme?.neutral ? "neutral" : undefined
  ]
    .filter((s): s is keyof DictsysInflectionSchemeGermanNounGenders => Boolean(s))
    .map((g) => buildGenderHeader(wordClassLevel.value, g));

  const countTypeHeaders = isMultiGenderScheme
    ? [buildCountTypeHeader(wordClassLevel.value, "plural")]
    : [
        buildCountTypeHeader(wordClassLevel.value, "singular"),
        buildCountTypeHeader(wordClassLevel.value, "plural")
      ];

  const headerCells = isMultiGenderScheme
    ? [...genderHeaders, ...countTypeHeaders]
    : countTypeHeaders;
  const headerCellsFormatted = headerCells.map((cell) => ({ value: cell }));

  const bodyRowNominative = makeCaseRow(genderSchemes, articleType, "nominative");
  const bodyRowAccusative = makeCaseRow(genderSchemes, articleType, "akkusative");
  const bodyRowGenitive = makeCaseRow(genderSchemes, articleType, "genitive");
  const bodyRowDative = makeCaseRow(genderSchemes, articleType, "dative");

  return {
    columns: [{ value: "Kasus" }, ...headerCellsFormatted],
    rows: [
      { cells: [makeInflectionCell(casusLabels["nominative"]), ...bodyRowNominative] },
      { cells: [makeInflectionCell(casusLabels["akkusative"]), ...bodyRowAccusative] },
      { cells: [makeInflectionCell(casusLabels["genitive"]), ...bodyRowGenitive] },
      { cells: [makeInflectionCell(casusLabels["dative"]), ...bodyRowDative] }
    ]
  };
};

const schemes = computed<InflectionScheme<ArticleType>[]>(() => {
  const definiteTable = makeArticleTypeTable(props.inflectionScheme, "definite");
  const indefiniteTable = makeArticleTypeTable(props.inflectionScheme, "indefinite");

  return [
    {
      key: "definite",
      table: definiteTable
    },
    {
      key: "indefinite",
      table: indefiniteTable
    }
  ];
});

const { articleFontClass } = useArticleFont();
</script>

<template>
  <div>
    <InflectionSchemeTabs
      :tabs="countTypeTabs"
      :active-tab="activeCountTypeTabKey"
      @show-tab="activeCountTypeTabKey = $event"
    />
    <div v-for="scheme in schemes" :key="scheme.key">
      <AbcResponsiveTable
        v-if="scheme.key === activeCountTypeTabKey"
        :columns="scheme.table.columns"
        :rows="scheme.table.rows"
        desktop-breakpoint="lg"
        :column-widths="[{ xs: '95px', xl: '128px' }]"
      >
        <template #head-cell="{ cell }">
          <span :class="articleFontClass">{{ cell }}</span>
        </template>
        <template #body-cell="{ cell, isFirst }">
          <!-- First cell of each row is actually the case name -->
          <InflectionSchemeCell v-if="isFirst" :content="cell[0].inflexion" />
          <slot v-else name="inflection-cell" v-bind="{ inflections: cell }">
            <InflectionSchemeCell :content="cell" />
          </slot>
        </template>
      </AbcResponsiveTable>
    </div>
  </div>
</template>
